import { getApiBaseUrl } from "config/api";
import {
  clearStoredTokens,
  getStoredAccessToken,
  getStoredRefreshToken,
  setStoredTokens,
} from "services/tokenStorage";
import { olvidarActividad, sesionInactiva } from "services/inactividadSesion";

function isAbsoluteUrl(value = "") {
  return /^https?:\/\//i.test(String(value || "").trim());
}

export function joinUrl(base, next = "") {
  const baseClean = String(base || "").replace(/\/+$/, "");
  const nextRaw = String(next || "");
  if (!nextRaw) {
    return baseClean;
  }
  if (isAbsoluteUrl(nextRaw)) {
    return nextRaw;
  }
  if (nextRaw.startsWith("?")) {
    return `${baseClean}${nextRaw}`;
  }
  return `${baseClean}/${nextRaw.replace(/^\/+/, "")}`;
}

function buildHeaders(customHeaders = {}, includeJson = true) {
  const headers = new Headers(customHeaders || {});
  const accessToken = getStoredAccessToken();
  if (accessToken && !headers.has("authorization")) {
    headers.set("authorization", `Bearer ${accessToken}`);
  }
  if (includeJson && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  return headers;
}

// ---------------------------------------------------------------------------
// Renovación silenciosa de la sesión.
//
// El access token dura 15 minutos y el refresh token 7 días, pero el refresh
// NUNCA se usaba: al vencer el access, la siguiente petición daba 401, la app
// borraba los tokens y echaba al usuario. En la práctica había que volver a
// entrar cada rato, aunque la sesión tuviera una semana de vida por delante.
//
// Ahora un 401 intenta renovar una vez y reintenta la petición original. Solo
// si la renovación falla se cierra la sesión de verdad.
// ---------------------------------------------------------------------------

// El backend ROTA el refresh token en cada uso: si dos peticiones vencen a la
// vez y refrescan en paralelo, la segunda llega con un token ya invalidado y
// cierra la sesión. Por eso todas comparten la misma promesa en vuelo.
let renovacionEnVuelo = null;

async function renovarSesion() {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return false;

  // Una sesión abandonada no se resucita. Sin esto la renovación silenciosa
  // deja la sesión viva los 7 días del refresh token: fue lo que hizo que
  // después de casi 4 horas sin tocar nada siguiera abierta.
  if (sesionInactiva()) {
    clearStoredTokens();
    olvidarActividad();
    return false;
  }

  if (!renovacionEnVuelo) {
    renovacionEnVuelo = (async () => {
      try {
        // fetch directo, sin pasar por endpoints.js: ese módulo importa de
        // este, y llamarlo desde acá crearía una dependencia circular.
        const res = await fetch(joinUrl(getApiBaseUrl(), "/api/v1/auth/refresh"), {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        });
        if (!res.ok) return false;
        const data = await res.json().catch(() => null);
        const nuevoAccess = data?.tokens?.accessToken;
        if (!nuevoAccess) return false;
        // El refresh nuevo se guarda sí o sí: el anterior quedó invalidado.
        setStoredTokens(nuevoAccess, data?.tokens?.refreshToken || "");
        return true;
      } catch {
        return false;
      } finally {
        renovacionEnVuelo = null;
      }
    })();
  }

  return renovacionEnVuelo;
}

/**
 * fetch con renovación: ante un 401 con refresh token guardado, renueva una vez
 * y reintenta. `construirOpciones` se vuelve a evaluar en el reintento para que
 * la cabecera lleve el token nuevo.
 */
async function fetchConRenovacion(url, construirOpciones) {
  const res = await fetch(url, construirOpciones());
  if (res.status !== 401 || !getStoredRefreshToken()) {
    return res;
  }
  const renovado = await renovarSesion();
  if (!renovado) {
    clearStoredTokens();
    return res;
  }
  return fetch(url, construirOpciones());
}

async function parseResponse(res) {
  const contentType = String(res.headers.get("content-type") || "").toLowerCase();
  if (contentType.includes("application/json")) {
    const data = await res.json().catch(() => null);
    return { res, data, text: "" };
  }
  const text = await res.text().catch(() => "");
  return { res, data: null, text };
}

async function request(method, url, { body, headers, includeJson = true } = {}) {
  const res = await fetchConRenovacion(url, () => ({
    method,
    headers: buildHeaders(headers, includeJson),
    body,
  }));
  return parseResponse(res);
}

export async function apiRequest(method, pathOrUrl, options = {}) {
  const baseUrl = getApiBaseUrl();
  const url = isAbsoluteUrl(pathOrUrl) ? pathOrUrl : joinUrl(baseUrl, pathOrUrl);
  return request(method, url, options);
}

export async function apiGet(pathOrUrl) {
  return apiRequest("GET", pathOrUrl, { includeJson: false });
}

export async function apiGetText(pathOrUrl) {
  return apiRequest("GET", pathOrUrl, { includeJson: false });
}

/** GET que devuelve un archivo binario (Blob), con auth. Para exports .xlsx, etc. */
export async function apiGetBlob(pathOrUrl) {
  const baseUrl = getApiBaseUrl();
  const url = isAbsoluteUrl(pathOrUrl) ? pathOrUrl : joinUrl(baseUrl, pathOrUrl);
  const res = await fetchConRenovacion(url, () => ({ method: "GET", headers: buildHeaders({}, false) }));
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    return { res, blob: null, data, filename: null };
  }
  const blob = await res.blob();
  const cd = String(res.headers.get("content-disposition") || "");
  const match = /filename="?([^";]+)"?/i.exec(cd);
  const filename = match ? match[1] : null;
  return { res, blob, data: null, filename };
}

/** POST que devuelve un archivo binario (Blob), con auth y cuerpo JSON. */
export async function apiPostBlob(pathOrUrl, payload = {}) {
  const baseUrl = getApiBaseUrl();
  const url = isAbsoluteUrl(pathOrUrl) ? pathOrUrl : joinUrl(baseUrl, pathOrUrl);
  const res = await fetchConRenovacion(url, () => ({
    method: "POST",
    headers: buildHeaders({}, true),
    body: JSON.stringify(payload ?? {}),
  }));
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    return { res, blob: null, data, filename: null };
  }
  const blob = await res.blob();
  const cd = String(res.headers.get("content-disposition") || "");
  const match = /filename="?([^";]+)"?/i.exec(cd);
  const filename = match ? match[1] : null;
  return { res, blob, data: null, filename };
}

export async function apiPost(pathOrUrl, payload = {}, options = {}) {
  return apiRequest("POST", pathOrUrl, {
    ...options,
    body: JSON.stringify(payload ?? {}),
  });
}

export async function apiPut(pathOrUrl, payload = {}, options = {}) {
  return apiRequest("PUT", pathOrUrl, {
    ...options,
    body: JSON.stringify(payload ?? {}),
  });
}

export async function apiPatch(pathOrUrl, payload = {}, options = {}) {
  return apiRequest("PATCH", pathOrUrl, {
    ...options,
    body: JSON.stringify(payload ?? {}),
  });
}

export async function apiDelete(pathOrUrl, payload = null, options = {}) {
  return apiRequest("DELETE", pathOrUrl, {
    ...options,
    body: payload == null ? null : JSON.stringify(payload),
  });
}

export async function apiPostForm(pathOrUrl, formData, options = {}) {
  return apiRequest("POST", pathOrUrl, {
    ...options,
    body: formData,
    includeJson: false,
  });
}

export async function apiPatchForm(pathOrUrl, formData, options = {}) {
  return apiRequest("PATCH", pathOrUrl, {
    ...options,
    body: formData,
    includeJson: false,
  });
}
